/**
 * Copyright Warner Bros. Entertainment, Inc.
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains the property
 * of Warner Bros. Entertainment, Inc. and its suppliers, if any.
 * The intellectual and technical concepts contained herein are
 * proprietary to Warner Bros. Entertainment, Inc. and its suppliers
 * and may be covered by U.S. and Foreign Patents, patents in process,
 * and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is
 * unlawful and strictly forbidden unless prior written permission is
 * obtained from Warner Bros. Entertainment, Inc.
 */

import Immutable from 'immutable';
import PropTypes from 'prop-types';
import React, {Component} from 'react';
import {Button, Glyphicon} from 'react-bootstrap';

import SynopsisDetail, {Description} from './synopsis-detail';
import {DownloadActions} from '../../../../common/download/download-actions';
import WithPermissions from '../../../../decorators/with-permissions';
import {MessagesContext} from '../../../../messages/messages-context';
import SessionStore from '../../../../session/session-store';
import {Compare, CompareImmutable} from '../../../../utils/utils';
import {shouldHideSynopses, TitleActions, TitleConstants} from '../../../title-actions';
import TitleModal from '../../modal';
import CastAndCredits from '../cast-and-credits';
import ButtonStyles from '../title-info.module.less';

class Synopsis extends Component {
    static get propTypes() {
        return {
            canAddToWorkOrder: PropTypes.bool,
            canDownloadSalesDeck: PropTypes.bool,
            canSelectDocuments: PropTypes.bool,
            canShareTitle: PropTypes.bool,
            canSubscribeClient: PropTypes.bool,
            canViewFrontendComment: PropTypes.bool,
            canViewFullEpisode: PropTypes.bool,
            canViewLanguageAvailability: PropTypes.bool,
            canViewMultipleReleaseDates: PropTypes.bool,
            canViewReleaseDate: PropTypes.bool,
            episodes: PropTypes.instanceOf(Immutable.List),
            genres: PropTypes.instanceOf(Immutable.List).isRequired,
            handleToggleExpandReleaseDatesTable: PropTypes.func,
            permissions: PropTypes.object.isRequired,
            related: PropTypes.instanceOf(Immutable.List),
            releaseDate: PropTypes.string,
            releaseDateType: PropTypes.number,
            releaseDatesTableCollapsed: PropTypes.bool,
            releaseTerritory: PropTypes.string,
            seasons: PropTypes.instanceOf(Immutable.List),
            serieNavigation: PropTypes.instanceOf(Immutable.Map),
            synopses: PropTypes.instanceOf(Immutable.List),
            synopsisUsageType: PropTypes.string,
            themes: PropTypes.instanceOf(Immutable.List).isRequired,
            title: PropTypes.instanceOf(Immutable.Map).isRequired,
            // TODO: remove talent and request them on CastAndCredits component to avoid request
            // talent in pages that are not using them (like all pages that are not info)
            titleTalent: PropTypes.instanceOf(Immutable.List).isRequired,
            videos: PropTypes.instanceOf(Immutable.List).isRequired,
        };
    }

    static get defaultProps() {
        return {
            canAddToWorkOrder: false,
            canDownloadSalesDeck: false,
            canSelectDocuments: false,
            canShareTitle: false,
            canSubscribeClient: false,
            canViewFrontendComment: false,
            canViewFullEpisode: false,
            canViewLanguageAvailability: false,
            canViewMultipleReleaseDates: false,
            canViewReleaseDate: false,
            episodes: undefined,
            handleToggleExpandReleaseDatesTable: /* istanbul ignore next */() => void 0,
            related: undefined,
            releaseDate: '',
            releaseDateType: undefined,
            releaseDatesTableCollapsed: true,
            releaseTerritory: '',
            seasons: '',
            serieNavigation: '',
            synopses: Immutable.List(),
            synopsisUsageType: undefined,
        };
    }

    static getPermissions() {
        return {
            canDownloadAllFactSheets: SessionStore.canUser(SessionStore.PERMISSIONS.TITLE.FACT_SHEETS.DOWNLOAD_ALL),
            canDownloadFactSheet: SessionStore.canUser(SessionStore.PERMISSIONS.TITLE.FACT_SHEETS.DOWNLOAD),
        };
    }

    static getStores() {
        return [SessionStore];
    }

    constructor(props) {
        super(props);

        this.state = {
            showSynopsesModal: false,
            showWorkOrderModal: false,
        };

        this.closeModal = this.closeModal.bind(this);
        this.getAlternativeSynopsis = this.getAlternativeSynopsis.bind(this);
        this.handleDownloadFactSheet = this.handleDownloadFactSheet.bind(this);
        this.openModal = this.openModal.bind(this);
    }

    shouldComponentUpdate(nextProps, nextState) {
        return !Compare(
            this.props, nextProps, [
                'canAddToWorkOrder',
                'canDownloadSalesDeck',
                'canShareTitle',
                'canSubscribeClient',
                'canViewFullEpisode',
                'genres',
                'related',
                'releaseDate',
                'releaseDateType',
                'releaseDatesTableCollapsed',
                'releaseTerritory',
                'serieNavigation',
                'themes',
                'titleTalent',
                'videos'
            ]) ||
            !CompareImmutable(this.props.title, nextProps.title, ['id', 'defaultImagePreviewUrl']) ||
            !Compare(this.state, nextState, ['showWorkOrderModal', 'showSynopsesModal']);
    }

    static contextType = MessagesContext;

    closeModal(modalName, event) {
        if (event) {
            event.preventDefault();
        }

        this.setState({['show' + modalName + 'Modal']: false});
    }

    getAlternativeSynopsis(synopses, synopsisUsageType) {
        const alternativeSynopsis = synopses.find(s => s.get('usageType') === synopsisUsageType);
        return alternativeSynopsis?.get('synopsis');
    }

    handleDownloadAllFactSheet(titleId) {
        TitleActions.downloadAllFactSheet(titleId);
    }

    handleDownloadFactSheet() {
        const {title} = this.props;
        DownloadActions.startDownloadExecution(`title/${title.get('id')}/fact-sheet`);
    }

    openModal(modalName, event) {
        if (event) {
            event.preventDefault();
        }
        this.setState({['show' + modalName + 'Modal']: true});
    }

    render() {
        const {
            canAddToWorkOrder,
            canDownloadSalesDeck,
            canSelectDocuments,
            canShareTitle,
            canSubscribeClient,
            canViewFrontendComment,
            canViewFullEpisode,
            canViewLanguageAvailability,
            canViewMultipleReleaseDates,
            canViewReleaseDate,
            episodes,
            genres,
            handleToggleExpandReleaseDatesTable,
            permissions,
            related,
            releaseDate,
            releaseDateType,
            releaseDatesTableCollapsed,
            releaseTerritory,
            seasons,
            serieNavigation,
            synopses,
            synopsisUsageType,
            themes,
            title,
            titleTalent,
            videos
        } = this.props;
        const {showSynopsesModal, showWorkOrderModal} = this.state;
        const {intl} = this.context;
        const synopsesAreHidden = shouldHideSynopses(title);

        let synopsisText = title.get('synopsis') || intl.messages['title.synopsis.not-available'];

        if (synopsisUsageType) {
            const altSynopsisText = this.getAlternativeSynopsis(synopses, synopsisUsageType);
            // falls back to default title synopsis if alternative synopsis is unavailable
            if (altSynopsisText !== undefined && altSynopsisText !== '' && altSynopsisText !== null) {
                synopsisText = altSynopsisText;
            }
        }

        if (synopsesAreHidden && (synopsisUsageType !== TitleConstants.SYNOPSIS_TYPES.SCREENER.id)) {
            synopsisText = intl.messages['title.synopsis.not-available'];
        }

        let hr;
        let description;
        let rightPanel = <Description className="col-md-6 padding-y-20" synopsis={synopsisText} />;

        let awards, awardsOnRight;
        if (title.get('awards')) {
            awardsOnRight = (
                <div>
                    <h3>Awards</h3>
                    <p dangerouslySetInnerHTML={{__html: title.get('awards')}} />
                </div>
            );
        }

        const talentByRole = titleTalent.reduce((tr, t) => {
            const role = t.get('roleInTitle');
            const talent = tr.get(role, Immutable.List());
            return tr.set(role, talent.push(t));
        }, Immutable.Map());

        if (titleTalent.size !== 0) {
            hr = <hr/>;
            description = <Description synopsis={synopsisText} />;
            rightPanel = <CastAndCredits talentByRole={talentByRole} title={title}/>;
            // Display awards on left side below the synopsis.
            awards = awardsOnRight;
            awardsOnRight = undefined;
        }

        let fsDownload;
        if (permissions.canDownloadFactSheet && title.get('category') !== TitleConstants.TITLE_TYPES.EVENT.id) {
            fsDownload = (
                <Button className="btn-secondary" onClick={this.handleDownloadFactSheet}>
                    <Glyphicon glyph="download-alt" /><span>{intl.messages['title.synopsis.download.fact-sheet']}</span>
                </Button>
            );
        }

        let fsDownloadAll;
        let addToWorkOrderBtn;
        let addToWorkOrderModal;

        if (canAddToWorkOrder || canDownloadSalesDeck) {
            addToWorkOrderModal = (
                <TitleModal.WorkOrder
                    close={this.closeModal.bind(this, 'WorkOrder')}
                    serieNavigation={serieNavigation}
                    show={showWorkOrderModal}
                    title={title}
                />
            );
            addToWorkOrderBtn = (
                <Button className="btn-secondary" onClick={this.openModal.bind(this, 'WorkOrder')}>
                    <Glyphicon glyph="copy" />
                    <span>{intl.messages['title.synopsis.work-order.add']}</span>
                </Button>
            );
        }

        if (permissions.canDownloadAllFactSheets &&
            [
                TitleConstants.TITLE_CATEGORY_GROUPS.MINI_SERIES,
                TitleConstants.TITLE_CATEGORY_GROUPS.SERIES,
                TitleConstants.TITLE_CATEGORY_GROUPS.SEASON
            ].indexOf(title.get('categoryGroup')) !== -1
        ) {
            fsDownloadAll = (
                <Button
                    className="btn-secondary"
                    onClick={this.handleDownloadAllFactSheet.bind(this, title.get('id'))}
                >
                    <Glyphicon glyph="download-alt" />
                    <span>{intl.messages['title.synopsis.download.fact-sheet-all']}</span>
                </Button>
            );
        }
        const additionalSynopsesBtn = (
            <div>
                <Button bsStyle="link" className={ButtonStyles['btn-open-modal']} onClick={this.openModal.bind(this, 'Synopses')}>
                    {intl.messages['title.synopsis.view-additional-synopses']}
                    <Glyphicon glyph="chevron-right" />
                </Button>
            </div>
        );
        const additionalSynopsesModal = (
            <TitleModal.Synopses
                close={this.closeModal.bind(this, 'Synopses')}
                show={showSynopsesModal}
                synopses={synopses}
                synopsesAreHidden={synopsesAreHidden}
                synopsisUsageType={synopsisUsageType}
                title={title}
            />
        );

        return (
            <div className="container padding-y-10 padding-x-10">
                <div className="row">
                    {additionalSynopsesModal}
                    {addToWorkOrderModal}
                    <div className="col-md-6">
                        <div className="row">
                            <div className="col-md-12 padding-x-10 padding-y-10">
                                <SynopsisDetail
                                    canSelectDocuments={canSelectDocuments}
                                    canShareTitle={canShareTitle}
                                    canSubscribeClient={canSubscribeClient}
                                    canViewFrontendComment={canViewFrontendComment}
                                    canViewFullEpisode={canViewFullEpisode}
                                    canViewLanguageAvailability={canViewLanguageAvailability}
                                    canViewMultipleReleaseDates={canViewMultipleReleaseDates}
                                    canViewReleaseDate={canViewReleaseDate}
                                    episodeListId="episodeListContainer"
                                    episodes={episodes}
                                    genres={genres}
                                    handleToggleExpandReleaseDatesTable={handleToggleExpandReleaseDatesTable}
                                    related={related}
                                    releaseDate={releaseDate}
                                    releaseDateType={releaseDateType}
                                    releaseDatesTableCollapsed={releaseDatesTableCollapsed}
                                    releaseTerritory={releaseTerritory}
                                    seasonListId="seasonListContainer"
                                    seasons={seasons}
                                    serieNavigation={serieNavigation}
                                    themes={themes}
                                    title={title}
                                    videos={videos}
                                />
                                {hr}
                                {description}
                                {additionalSynopsesBtn}
                                {fsDownload}
                                {fsDownload && <span>&nbsp;</span>}
                                {fsDownloadAll}
                                {fsDownloadAll && <span>&nbsp;</span>}
                                {addToWorkOrderBtn}
                                {awards}
                            </div>
                        </div>
                    </div>
                    {rightPanel}
                    {awardsOnRight}
                </div>
            </div>
        );
    }
}

export default WithPermissions(Synopsis);
export {Synopsis};
